import { useState, useEffect, useMemo, useRef } from 'react';
import { useDebounce } from "@uidotdev/usehooks";
import {
  useUiPreferences,
  createUiPreference,
  updateUiPreference,
} from '@api';


const buildDefaultPreferences = columns =>
  columns.map((col, index) => ({
    id: col.id,
    title: col.title,
    // Columns that start switched off. Unlike `col.hide`, which withdraws a column altogether,
    // this only sets the initial preference: the customisation list still offers it.
    hide: !!col.hiddenByDefault,
    position: index,
    // Only carried when true, so stored preferences stay as they were for ordinary columns.
    // Tells the customisation list the column is never drawn and so has no position to drag
    // it to. Derived from the column on every reconcile, never trusted from storage.
    ...(col.filterOnly ? { filterOnly: true } : {}),
  }));

const arePreferencesEqual = (a, b) =>
  a.length === b.length &&
  a.every((p, i) =>
    ["id", "title", "hide", "position"].every((k) => p[k] === b[i][k]),
  );

const reconcilePreferencesWithColumns = (preferences, columns) => {
  const existingPreferences = preferences
    .filter(pref => columns.some(col => col.id === pref.id))
    .map(pref => {
      const col = columns.find(c => c.id === pref.id);
      if (!col) return pref;
      // Title and filterOnly are re-derived from the column rather than taken from storage, so
      // a renamed or repurposed column is never described by a stale saved preference. The
      // caller compares by value before setting state, so rebuilding the object is free.
      const { filterOnly, ...stored } = pref;
      return {
        ...stored,
        title: col.title,
        ...(col.filterOnly ? { filterOnly: true } : {}),
      };
    });

  const preferenceIds   = new Set(existingPreferences.map(p => p.id));
  const newColumnPrefs  = columns
    .filter(col => !preferenceIds.has(col.id))
    .map((col) => ({
      id: col.id,
      title: col.title,
      hide: !!col.hiddenByDefault,
      position: columns.findIndex(c => c.id === col.id),
      ...(col.filterOnly ? { filterOnly: true } : {}),
    }));


  const columnOrder = Object.fromEntries(
    columns.map((c, i) => [c.id, i])
  );

  return [...existingPreferences, ...newColumnPrefs]
  .sort(
    (a, b) =>
      a.position - b.position ||
      columnOrder[a.id] - columnOrder[b.id]
  )
  .map((pref, idx) => ({ ...pref, position: idx }));
};




const useColumnManager = (tableColumns, tableId) => {

  const columnType = `column:${tableId}`;

  const [columnPreferences, setColumnPreferences] = useState(() =>
    buildDefaultPreferences(tableColumns),
  );

  const preferenceIdRef = useRef(null);
  const isSavingRef    = useRef(false);
  const pendingPrefsRef = useRef(null);
  const lastAppliedColumnTypeRef = useRef(null);
  const [hasHydrated, setHasHydrated] = useState(false);

  const { data: allPreferences } = useUiPreferences();

  useEffect(() => {
    if (!allPreferences) return;
    setHasHydrated(true);
    if (lastAppliedColumnTypeRef.current === columnType) return;
    lastAppliedColumnTypeRef.current = columnType;
    preferenceIdRef.current = null;

    const saved = allPreferences.find((p) => p.type === columnType);
    if (saved) {
      try {
        const parsed = JSON.parse(saved.value);
        if (Array.isArray(parsed)) {
          const reconciled = reconcilePreferencesWithColumns(parsed, tableColumns);
          setColumnPreferences(reconciled);
          lastSavedPrefs.current = reconciled;
        }
      } catch (err) {
        console.error("Failed to parse column prefs", err);
      }
      preferenceIdRef.current = saved.id;
    }
  }, [allPreferences, columnType]);

  useEffect(() => {
    setColumnPreferences((prev) => {
      const next = reconcilePreferencesWithColumns(prev, tableColumns);
      return arePreferencesEqual(prev, next) ? prev : next;
    });
  }, [tableColumns]);

  const lastSavedPrefs = useRef(columnPreferences);

  const saveColumnPreferences = async (prefs) => {
    pendingPrefsRef.current = prefs;
    if (isSavingRef.current) return;

    while (pendingPrefsRef.current) {
      const toSave = pendingPrefsRef.current;
      pendingPrefsRef.current = null;
      isSavingRef.current = true;
      const value = JSON.stringify(toSave);
      try {
        if (preferenceIdRef.current) {
          await updateUiPreference(preferenceIdRef.current, { name: columnType, value });
        } else {
          const created = await createUiPreference({ name: columnType, type: columnType, value });
          if (created) preferenceIdRef.current = created.id;
        }
        lastSavedPrefs.current = toSave;
      } catch (err) {
        console.error('Failed to save column prefs', err);
        break;
      } finally {
        isSavingRef.current = false;
      }
    }
  };

  const debouncedPrefs = useDebounce(columnPreferences, 200);

  useEffect(() => {
    // Never persist before the server preferences have loaded: the initial state
    // is the default order, and saving it would clobber the user's saved layout.
    if (!hasHydrated) return;
    // Only persist once the debounce has settled to the current state. While it
    // still lags behind (e.g. right after hydration applies the saved order),
    // debouncedPrefs holds a stale snapshot that must not be written back.
    if (!arePreferencesEqual(debouncedPrefs, columnPreferences)) return;
    if (arePreferencesEqual(lastSavedPrefs.current, debouncedPrefs)) return;
    saveColumnPreferences(debouncedPrefs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPrefs, columnPreferences, hasHydrated]);

  const resetColumns = () => setColumnPreferences(buildDefaultPreferences(tableColumns));

  const managedColumns = useMemo(() => {
    const visibleColumns = columnPreferences
      .filter(pref => !pref.hide)
      .sort((a, b) => a.position - b.position)
      .map(pref => tableColumns.find(col => col.id === pref.id))
      .filter(Boolean);

    return visibleColumns.length ? visibleColumns : tableColumns;
  }, [columnPreferences, tableColumns]);

  return {
    columnPreferences,
    setColumnPreferences,
    managedColumns,
    resetColumns
  };
};

export default useColumnManager;
